/**
 * Sanitize and validate storage keys to prevent XSS
 * @param key - The storage key to validate
 * @returns Sanitized key
 * @throws Error if key is invalid
 */
function validateStorageKey(key: string): string {
  if (!key || typeof key !== 'string') {
    throw new Error('Storage key must be a non-empty string');
  }

  // Remove potentially dangerous characters and limit length
  const sanitized = key
    .replace(/[<>'"&]/g, '') // Remove HTML/JS injection chars
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/[^\w\-_.]/g, '') // Only allow safe characters
    .trim()
    .substring(0, 100); // Limit key length

  if (!sanitized) {
    throw new Error('Storage key contains only invalid characters');
  }

  return sanitized;
}

/**
 * Sanitize and validate storage values to prevent XSS
 * @param value - The storage value to validate
 * @returns Sanitized value
 * @throws Error if value is invalid
 */
function validateStorageValue(value: string): string {
  if (typeof value !== 'string') {
    throw new Error('Storage value must be a string');
  }

  // Limit value size (localStorage has limits anyway)
  if (value.length > 1024 * 1024) {
    // 1MB limit
    throw new Error('Storage value is too large');
  }

  // Additional XSS protection - escape dangerous patterns
  const sanitized = value
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/on\w+\s*=/gi, ''); // Remove event handlers

  return sanitized;
}

export function setLocalStorageItem(key: string, value: string): void {
  try {
    const validatedKey = validateStorageKey(key);
    const validatedValue = validateStorageValue(value);
    const encodedValue = encodeURIComponent(validatedValue);

    localStorage.setItem(validatedKey, encodedValue);
  } catch (error) {
    console.error('Failed to set localStorage item:', error);
    throw error;
  }
}

export function getLocalStorageItem(key: string): string | null {
  try {
    const validatedKey = validateStorageKey(key);
    const item = localStorage.getItem(validatedKey);

    if (!item) {
      return null;
    }

    const decoded = decodeURIComponent(item);
    return decoded;
  } catch (error) {
    console.error('Failed to get localStorage item:', error);
    return null;
  }
}
